import React from "react";
import clsx from "clsx";

import { IAssessmentStudent } from "../../../state";
import { formatDate } from "../../../libs/Util";

import styles from "./AssessmentStudentSummary.module.scss";

export type Props = {
  student: IAssessmentStudent;
  selectedExperienceNo: number;
  onClick: () => void;
};

const AssessmentStudentSummary: React.FC<Props> = ({
  student,
  selectedExperienceNo,
  onClick,
}) => {
  const { term_no, assessment_created_at, has_next_actions } = student;

  const currentScore = React.useMemo(() => {
    const value =
      selectedExperienceNo === 0
        ? student.avg_score
        : student.assessment_experiences?.find(
            a => a.experience_no === selectedExperienceNo,
          )?.score;
    return value ?? null;
  }, [student, selectedExperienceNo]);

  const scoreText = React.useMemo(() => {
    return currentScore === null ? "" : currentScore.toFixed(1);
  }, [currentScore]);

  const difference = React.useMemo(() => {
    const value =
      selectedExperienceNo === 0
        ? student.avg_difference
        : student.assessment_experiences?.find(
            a => a.experience_no === selectedExperienceNo,
          )?.difference;
    return value ?? null;
  }, [student, selectedExperienceNo]);

  return (
    <div className={styles.studentWrapper} onClick={onClick}>
      <div className={styles.topArea}>
        {term_no !== null && <span>{`${term_no}回目`}</span>}
        {assessment_created_at !== null && (
          <span>{formatDate(new Date(assessment_created_at), "/")}</span>
        )}
      </div>
      <span className={styles.studentName}>{student.full_name}</span>
      <div
        className={clsx(
          styles.scoreArea,
          difference !== null && difference > 0 && styles.high,
          difference !== null && difference < 0 && styles.low,
        )}
      >
        <div className={styles.average}>
          {selectedExperienceNo === 0 ? (
            <div className={styles.title}>
              平均
              <br />
              スコア
            </div>
          ) : (
            <div className={styles.title}>
              スコア
              <br />
              &nbsp;
            </div>
          )}
          {!scoreText ? (
            <span className={styles.score}>&nbsp;-</span>
          ) : (
            <span className={styles.score}>
              {scoreText.substring(0, 1)}
              <span className={styles.sub}>{scoreText.substring(1)}</span>
            </span>
          )}
        </div>
        <div className={styles.difference}>
          <span className={styles.title}>前回差</span>
          {difference === null ? (
            <span className={styles.score}>-&nbsp;</span>
          ) : (
            <span className={styles.score}>{`${
              difference > 0 ? "+" : ""
            }${difference.toFixed(1)}`}</span>
          )}
        </div>
      </div>
      <div className={styles.nextActionArea}>
        <span className={styles.nextActionTitle}>まとめ入力</span>
        <span
          className={clsx(
            styles.nextActionLabel,
            !has_next_actions && styles.low,
          )}
        >
          {!has_next_actions
            ? "記入なし"
            : has_next_actions < 3
            ? "一部記入"
            : "完了"}
        </span>
      </div>
    </div>
  );
};

export default AssessmentStudentSummary;
